import React, { useState, useEffect } from "react";
import "../styles/OpenDiscountModal.css";

const OpenDiscountModal = ({ 
  show, 
  onClose, 
  onApplyDiscount, 
  product, 
  selectedItemIndex, 
  order,
  calculateItemTotal
}) => {
  const [discountAmount, setDiscountAmount] = useState("");
  const [discountName, setDiscountName] = useState("Descuento Abierto");
  const [error, setError] = useState(null);
  const [productInfo, setProductInfo] = useState("");
  const [discountType, setDiscountType] = useState("percentage");
  const [previewAmount, setPreviewAmount] = useState(0);

  // Función para calcular el máximo descuento permitido
  const calculateMaxDiscount = () => {
    if (product && selectedItemIndex !== null && selectedItemIndex !== undefined) {
      const itemPrice = product.price || 0;
      const modifiersTotal = product.modifiers 
        ? product.modifiers.reduce((total, mod) => total + (mod.price * (mod.quantity || 1)), 0)
        : 0;
      return (itemPrice + modifiersTotal) * (product.quantity || 1);
    } else {
      if (!order || order.length === 0) return 0;
      
      return order.reduce((total, item) => {
        const itemPrice = item.price || 0;
        const modifiersTotal = item.modifiers 
          ? item.modifiers.reduce((modTotal, mod) => modTotal + (mod.price * (mod.quantity || 1)), 0)
          : 0;
        return total + ((itemPrice + modifiersTotal) * (item.quantity || 1));
      }, 0);
    }
  };

  // Calcular vista previa del descuento
  useEffect(() => {
    if (!discountAmount || parseFloat(discountAmount) <= 0) {
      setPreviewAmount(0);
      return;
    }

    const discountValue = parseFloat(discountAmount);
    let preview = 0;

    if (discountType === "percentage") {
      const maxDiscount = calculateMaxDiscount();
      preview = maxDiscount * (discountValue / 100);
    } else {
      preview = Math.min(discountValue, calculateMaxDiscount());
    }

    setPreviewAmount(parseFloat(preview.toFixed(2)));
  }, [discountAmount, discountType, product, order, selectedItemIndex]);

  // Actualizar información del producto
  useEffect(() => {
    if (product && selectedItemIndex !== null && selectedItemIndex !== undefined) {
      const modifiersTotal = product.modifiers 
        ? product.modifiers.reduce((total, mod) => total + (mod.price * (mod.quantity || 1)), 0)
        : 0;
      const totalWithModifiers = (product.price + modifiersTotal) * (product.quantity || 1);
      
      setProductInfo(`Aplicando descuento a: ${product.name} - $${totalWithModifiers.toFixed(2)} (incluye modificadores)`);
    } else {
      const orderTotal = order.reduce((total, item) => {
        const itemPrice = item.price || 0;
        const modifiersTotal = item.modifiers 
          ? item.modifiers.reduce((modTotal, mod) => modTotal + (mod.price * (mod.quantity || 1)), 0)
          : 0;
        return total + ((itemPrice + modifiersTotal) * (item.quantity || 1));
      }, 0);
      
      setProductInfo(`Descuento general al pedido completo - Total: $${orderTotal.toFixed(2)} (incluye modificadores)`);
    }
  }, [product, selectedItemIndex, order]);

  const handleKeyPress = (key) => {
    let newValue = discountAmount;

    if (key === "C") {
      newValue = "";
    } else if (key === "⌫") {
      newValue = discountAmount.slice(0, -1);
    } else if (key === "." && discountAmount.includes(".")) {
      return;
    } else if (discountAmount === "" && key === ".") {
      newValue = "0.";
    } else {
      newValue += key;
    }

    setDiscountAmount(newValue);
    validateInput(newValue);
  };

  const validateInput = (value) => {
    if (value === "" || parseFloat(value || 0) <= 0) {
      setError("Por favor, ingrese un monto válido mayor a 0.");
      return false;
    }

    if (discountType === "percentage" && parseFloat(value) > 100) {
      setError("El porcentaje de descuento no puede exceder 100%.");
      return false;
    }

    const currentMaxDiscount = calculateMaxDiscount();
    if (discountType === "amount" && parseFloat(value) > currentMaxDiscount) {
      setError(`El descuento no puede exceder el máximo permitido: $${currentMaxDiscount.toFixed(2)}`);
      return false;
    }

    setError(null);
    return true;
  };

  const handleAccept = () => {
    if (!validateInput(discountAmount)) {
      return;
    }

    const discountValue = parseFloat(discountAmount);

    const discountData = {
      nombre_descuento: discountName || "Descuento Abierto",
      monto_descuento: discountValue,
      tipo_descuento: discountType,
      valor_original: discountValue,
      es_descuento_manual: true
    };

    if (onApplyDiscount) {
      onApplyDiscount(discountData, selectedItemIndex);
    }

    setDiscountAmount("");
    setDiscountName("Descuento Abierto");
    setDiscountType("percentage");
    setPreviewAmount(0);
    onClose();
  };

  const handleNameChange = (e) => {
    setDiscountName(e.target.value);
  };

  const handleDiscountTypeChange = (e) => {
    setDiscountType(e.target.value);
    setDiscountAmount("");
    setPreviewAmount(0);
    setError(null);
  };

  const handleRemoveDiscount = () => {
    const discountData = {
      nombre_descuento: "Sin descuento",
      monto_descuento: 0,
      tipo_descuento: discountType,
      valor_original: 0,
      es_descuento_manual: true,
      remover_descuento: true
    };
    if (onApplyDiscount) {
      onApplyDiscount(discountData, selectedItemIndex);
    }
    setDiscountAmount("");
    setPreviewAmount(0);
    onClose();
  };

  if (!show) return null;

  const keypadButtons = ["7", "8", "9", "4", "5", "6", "1", "2", "3", "0", ".", "⌫", "C"];
  const maxDiscount = calculateMaxDiscount();

  return (
    <div className="open-discount-modal-overlay">
      <div className="open-discount-modal-content">
        <h3>Descuento Abierto</h3>

        <div className="discount-type-selector">
          <label>
            <input
              type="radio"
              value="percentage"
              checked={discountType === "percentage"}
              onChange={handleDiscountTypeChange}
            />
            Porcentaje %
          </label>
          <label>
            <input
              type="radio"
              value="amount"
              checked={discountType === "amount"}
              onChange={handleDiscountTypeChange}
            />
            Monto $
          </label>
        </div>

        <p className="max-discount-info">
          {discountType === "percentage"
            ? "Ingrese el porcentaje de descuento"
            : "Ingrese el monto del descuento"}
          {` (máximo: $${maxDiscount.toFixed(2)})`}
        </p>

        {productInfo && <div className="product-info">{productInfo}</div>}

        <div className="discount-name-input">
          <label htmlFor="discountName">Nombre del descuento:</label>
          <input
            type="text"
            id="discountName"
            value={discountName}
            onChange={handleNameChange}
            placeholder="Nombre del descuento"
          />
        </div>

        <div className="discount-display">
          {discountType === "percentage" ? `${discountAmount || "0"}%` : `$${discountAmount || "0.00"}`}
        </div>

        <div className="discount-preview">
          <strong>Descuento aplicado:</strong> ${previewAmount.toFixed(2)}
        </div>

        {error && <p className="error-message">{error}</p>}

        <div className="keypad-container" data-no-vkb>
          <div className="keypad">
            {keypadButtons.map((key) => (
              <button
                key={key}
                className={`keypad-button ${key === "C" ? "clear-button" : key === "⌫" ? "backspace-button" : ""}`}
                onClick={() => handleKeyPress(key)}
              >
                {key}
              </button>
            ))}
            <button
              className="keypad-button remove-discount-button"
              onClick={handleRemoveDiscount}
            >
              🚫 Quitar Descuento
            </button>
          </div>
        </div>

        <div className="modal-actions">
          <button onClick={onClose} className="cancel-btn">
            Cancelar
          </button>
          <button
            onClick={handleAccept}
            className="confirm-btn"
            disabled={!discountAmount || error || !discountName.trim()}
          >
            Aplicar Descuento
          </button>
        </div>
      </div>
    </div>
  );
};

export default OpenDiscountModal;